import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { useCurrentUserSubscription } from '@/hooks/use-current-user-subscription';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { BackgroundImage, CheckIcon, Flex, Image, Text } from '@mantine/core';
import { IconHelpCircle } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import backgroundImage from './background-image.svg';
import { useFinaliseSubscriptionMutation } from './graphql.generated';
import mascotImage from './mascot-image.svg';

export const CheckoutSubscriptionResultPage = () => {
	const navigate = useNavigate();
	const { sessionId } = useParams({ from: '/_app/checkout-subscription-result/$sessionId' });
	const [finalisedSubscription, { loading, data, error }] = useFinaliseSubscriptionMutation();
	const { refetch } = useCurrentUserSubscription();

	/** Finalise subscription as soon as user visits this page */
	useEffect(() => {
		finalisedSubscription({ variables: { sessionId } });
	}, [finalisedSubscription, sessionId]);

	const subscriptionProduct = data?.finaliseSubscription;

	/** Update frontend data of user and subscription when we have got confirmation of a subscription product */
	useEffect(() => {
		if (subscriptionProduct?.id) {
			refetch();
		}
	}, [subscriptionProduct?.id, refetch]);

	const backToHome = useCallback(() => navigate({ to: '/home' }), [navigate]);
	const backToHomeButton = useMemo(
		() => <StandardButton onClick={backToHome}>Back to Home</StandardButton>,
		[backToHome]
	);

	const goToHelp = useCallback(() => navigate({ to: '/help' }), [navigate]);
	const helpButton = useMemo(
		() => (
			<CompactButton leftSection={<IconHelpCircle />} onClick={goToHelp}>
				Help
			</CompactButton>
		),
		[goToHelp]
	);

	return (
		<PrimaryLayout
			header={{ backTo: '/shop', title: 'Checkout', children: helpButton }}
			footer={{ children: backToHomeButton }}
		>
			<BackgroundImage src={backgroundImage} h="100vh">
				<ContentWithLoad
					loading={loading}
					error={!!error}
					isEmpty={!subscriptionProduct}
					isEmptyMessage="Unable to finalise subscription, please reload the app"
					isErrorMessage="An Error occurred"
				>
					<Flex direction="column" gap={50} align="center" mt={50}>
						<Image src={mascotImage} alt="Mascot" style={{ width: 200 }} />
						<Text ta="center" size="lg" fw={900} flex={1}>
							<CheckIcon size={17} color="green" /> You have successfully subscribed to{' '}
							{subscriptionProduct?.name}
						</Text>
					</Flex>
				</ContentWithLoad>
			</BackgroundImage>
		</PrimaryLayout>
	);
};
